import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import classNames from 'classnames';
import { useSelector, useDispatch } from 'react-redux';
import { isScrollingDisabled } from '../../ducks/UI.duck';
import { FormattedMessage } from '../../util/reactIntl';
import config from '../../config';
import {
    Page,
    Alert,
    LayoutSingleColumn,
    LayoutWrapperTopbar,
    LayoutWrapperMain,
    LayoutWrapperFooter,
    Footer,
    NoResultsBlock,
    IconSpinner,
    ManageListingCard,
    NamedRedirect,
    Button,
} from '../../components';
import { TopbarContainer } from '../../containers';
import { getOwnListingsById, loadData } from './ManageListingsPage.duck';

import css from './ManageListingsPage.css';
import ExtendListingLifetimeModal from './ExtendListingLifetimeModal';
import ErrorAlert from './ErrorAlert';
import { IconHighlightXL, IconMatchXL, IconTeamXL } from '../../icons';
import { createResourceLocatorString } from '../../util/routes';
import routeConfiguration from '../../routeConfiguration';
import { requestCreateListingDraft } from '../EditListingPage/EditListingPage.duck';
import { getListingNavParams } from '../../util/listings';
import {
    DESCRIPTION_RIDER,
    LISTING_PAGE_PARAM_TYPE_DRAFT,
} from '../../components/EditListingWizard/config';

const { userTypeRider } = config;

const ManageListingsPage = () => {
    const history = useHistory();
    const dispatch = useDispatch();

    const { location } = history;
    const onCreateListingDraft = values => dispatch(requestCreateListingDraft(values));

    const UI = useSelector(state => state['UI']);
    const currentUser = useSelector(state => state['user'].currentUser);
    const ManageListingsPage = useSelector(state => state['ManageListingsPage']);
    const createListingDraftInProgress = useSelector(
        state => state['EditListingPage'].createListingDraftInProgress
    );

    const {
        currentPageResultIds,
        queryInProgress,
        openingListingError,
        queryListingsError,
        closingListingError,
    } = ManageListingsPage;

    const listings = getOwnListingsById({ ManageListingsPage }, currentPageResultIds);
    const scrollingDisabled = isScrollingDisabled({ UI });

    const [noListingsAlert, setNoListingsAlert] = useState(true);
    const [filterListingsByState, setFilterListingsByState] = useState(null); // draft pendingApproval published closed
    const [activeControlPanelIndex, setActiveControlPanelIndex] = useState(-1);

    const navigate = (pageName, pageParams) =>
        history.push(createResourceLocatorString(pageName, routeConfiguration(), pageParams, {}));

    const handleNewListingCreation = async () => {
        try {
            const response = await onCreateListingDraft({});
            const newlyCreatedListing = response.data.data;

            const { uuid } = newlyCreatedListing.id;
            const { params } = getListingNavParams(newlyCreatedListing);

            navigate('EditListingPage', {
                slug: params.slug,
                id: uuid,
                type: LISTING_PAGE_PARAM_TYPE_DRAFT,
                tab: DESCRIPTION_RIDER,
            });
        } catch (e) {
            // to do
        }
    };

    useEffect(() => {
        if (!filterListingsByState) {
            return setNoListingsAlert(false);
        }

        setNoListingsAlert(
            listings.filter(
                ({ attributes: { state: listingState } }) => listingState === filterListingsByState
            ).length === 0
        );
    }, [filterListingsByState]);

    const noResults =
        !queryInProgress &&
        (!listings || (listings && listings.length === 0)) &&
        !queryListingsError;

    const dataAvailable =
        listings && listings.length > 0 && !queryListingsError && !queryInProgress;

    if (currentUser && currentUser.attributes.profile.publicData.userType === userTypeRider) {
        return <NamedRedirect name="LandingPage" />;
    }

    const actionButton = (
        <Button
            inProgress={createListingDraftInProgress}
            onClick={handleNewListingCreation}
            type="button"
        >
            Pferd hinzufügen
        </Button>
    );

    return (
        <Page title="Meine Pferde" scrollingDisabled={scrollingDisabled}>
            <LayoutSingleColumn>
                <LayoutWrapperTopbar>
                    <TopbarContainer currentPage="ManageListingsPage" />
                </LayoutWrapperTopbar>
                <LayoutWrapperMain
                    blobBackground
                    className={classNames(css.layoutWrapper, { [css.noResultsWrapper]: noResults })}
                >
                    {queryInProgress && <IconSpinner />}
                    {noResults && (
                        <>
                            <NoResultsBlock
                                headerId="Du hast noch kein Pferd hinzugefügt."
                                parapraphId="Erstelle ein Profil für dein Pferd und starte das Matching."
                                rootClass={css.noResults}
                                renderActionButton={({ containerClassName }) => (
                                    <div className={containerClassName}>{actionButton}</div>
                                )}
                            >
                                <div className={css.benefisRowContainer}>
                                    <div className={css.benefisItem}>
                                        <IconHighlightXL />
                                        <h5>Passende Unterstützung finden</h5>
                                        <p>
                                            Wir finden Pferdemenschen, die zu deinem Pferd und
                                            deinen Anforderungen passen.
                                        </p>
                                    </div>
                                    <div className={css.benefisItem}>
                                        <IconMatchXL />
                                        <h5>Übereinstimmung sehen</h5>
                                        <p>
                                            Erkenne auf einen Blick, wie gut ein Profil zu deinem
                                            Pferd passt.
                                        </p>
                                    </div>
                                    <div className={css.benefisItem}>
                                        <IconTeamXL />
                                        <h5>Direkt Kontakt aufnehmen</h5>
                                        <p>
                                            Kontaktiere passende Pferdemenschen in deiner Nähe.
                                            Kostenlos für immer.
                                        </p>
                                    </div>
                                </div>
                            </NoResultsBlock>
                            <footer className={css.noResultsMobActionSection}>
                                {actionButton}
                            </footer>
                        </>
                    )}
                    <ErrorAlert
                        queryListingsError={queryListingsError}
                        closingListingError={closingListingError}
                        openingListingError={openingListingError}
                    />
                    {dataAvailable && (
                        <main>
                            <header className={css.manageListingsHeading}>
                                <h3>
                                    <FormattedMessage id="ManageListingsPage.heading" />
                                </h3>
                                <p>
                                    <FormattedMessage id="ManageListingsPage.description" />
                                </p>
                            </header>
                            <nav className={css.statesNavBar}>
                                {['draft', 'pendingApproval', 'published', 'closed'].map(option => (
                                    <Button
                                        key={option}
                                        className={classNames(css.filterBadge, {
                                            [css.filterBadgeSelected]:
                                                filterListingsByState === option,
                                        })}
                                        onClick={() =>
                                            setFilterListingsByState(
                                                filterListingsByState === option ? null : option
                                            )
                                        }
                                    >
                                        <FormattedMessage
                                            id={`ManageListingPage.listingState-${option}`}
                                        />
                                    </Button>
                                ))}
                            </nav>
                            <section className={css.container}>
                                {listings.map((listing, index) =>
                                    filterListingsByState &&
                                    filterListingsByState !== listing.attributes.state ? null : (
                                        <ManageListingCard
                                            key={listing.id.uuid}
                                            filterListingsByState={filterListingsByState}
                                            index={index}
                                            listing={listing}
                                            controlPanelIndex={index}
                                            activeControlPanelIndex={activeControlPanelIndex}
                                            setActiveControlPanelIndex={setActiveControlPanelIndex}
                                        />
                                    )
                                )}
                                {noListingsAlert && (
                                    <Alert
                                        type="warning"
                                        header="ManageListingsPage.noListingsForAppliedFilterHeading"
                                        description="ManageListingsPage.noListingsForAppliedFilterDesc"
                                        rootClassName={css.alert}
                                    />
                                )}
                            </section>
                        </main>
                    )}
                    <ExtendListingLifetimeModal location={location} history={history} />
                </LayoutWrapperMain>
                <LayoutWrapperFooter>
                    <Footer />
                </LayoutWrapperFooter>
            </LayoutSingleColumn>
        </Page>
    );
};

ManageListingsPage.loadData = loadData;

export default ManageListingsPage;
